import { useState } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/context/AuthContext";

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-indigo-100 text-indigo-800",
  quoted: "bg-amber-100 text-amber-800",
  closed: "bg-emerald-100 text-emerald-800",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const Cell = ({ label, children, testId }) => (
  <div>
    <p className="label-xs">{label}</p>
    <div data-testid={testId} className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-ink">{children || "—"}</div>
  </div>
);

export const EnquiryRow = ({ item, onUpdated, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const updated = await authFetch(`/api/admin/enquiries/${item.id}`, { method: "PATCH", body: JSON.stringify(body) });
      onUpdated(updated);
      toast.success(okMsg);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete enquiry ${item.reference} from ${item.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await authFetch(`/api/admin/enquiries/${item.id}`, { method: "DELETE" });
      onDeleted(item.id);
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <li data-testid={`enquiry-row-${item.reference}`} className="bg-white">
      <button
        type="button"
        data-testid={`enquiry-toggle-${item.reference}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-cream md:grid-cols-[100px_1.2fr_1fr_1.2fr_150px_auto]"
      >
        <span data-testid={`enquiry-status-${item.reference}`} className={`rounded-full px-2.5 py-1 text-center text-xs font-semibold capitalize ${STATUS_STYLES[item.status] || STATUS_STYLES.new}`}>{item.status}</span>
        <span className="min-w-0">
          <span className="block truncate font-display text-base font-semibold">{item.name}</span>
          <span className="block truncate text-xs text-slate-500">{item.company || item.email}</span>
        </span>
        <span className="hidden truncate text-sm text-slate-600 md:block">{item.service}</span>
        <span className="hidden truncate text-sm text-slate-600 md:block">{item.budget || "Budget not stated"}</span>
        <span className="hidden text-xs text-slate-500 md:block">{fmtDate(item.timestamp)}</span>
        <span className="flex items-center gap-3">
          <span className="font-mono text-xs text-slate-500">{item.reference}</span>
          <ChevronDown size={16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div data-testid={`enquiry-details-${item.reference}`} className="border-t border-slate-200 bg-cream px-4 py-6 md:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            <Cell label="Email" testId="enquiry-detail-email"><a href={`mailto:${item.email}`} className="font-medium text-brand hover:underline">{item.email}</a></Cell>
            <Cell label="Phone" testId="enquiry-detail-phone">{item.phone ? <a href={`tel:${item.phone}`} className="font-medium text-brand hover:underline">{item.phone}</a> : null}</Cell>
            <Cell label="Company" testId="enquiry-detail-company">{item.company}</Cell>
            <Cell label="Service" testId="enquiry-detail-service">{item.service}</Cell>
            <Cell label="Budget" testId="enquiry-detail-budget">{item.budget}</Cell>
            <Cell label="Received" testId="enquiry-detail-date">{fmtDate(item.timestamp)}{item.consent ? " · Privacy consent given" : ""}</Cell>
            <div className="md:col-span-3"><Cell label="Project Requirements" testId="enquiry-detail-requirements">{item.requirements}</Cell></div>
            <div className="md:col-span-3"><Cell label="Message" testId="enquiry-detail-message">{item.message}</Cell></div>
          </div>

          <div className="mt-8 grid gap-6 border-t border-slate-200 pt-6 md:grid-cols-[1fr_auto]">
            <div>
              <p className="label-xs">Private note</p>
              <textarea
                data-testid="enquiry-note-input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Internal note — not visible to the client."
                className="field mt-2 text-sm"
              />
              <button
                type="button"
                data-testid="enquiry-save-note-button"
                disabled={busy || note === (item.note || "")}
                onClick={() => patch({ note }, "Note saved")}
                className="btn-outline mt-2 px-4 py-2 text-sm disabled:opacity-40"
              >
                Save Note
              </button>
            </div>
            <div className="flex flex-col gap-3">
              <p className="label-xs">Status</p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(STATUS_STYLES).map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`enquiry-set-status-${s}`}
                    disabled={busy || item.status === s}
                    onClick={() => patch({ status: s }, `Marked as ${s}`)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors disabled:opacity-50 ${item.status === s ? STATUS_STYLES[s] : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                type="button"
                data-testid="enquiry-delete-button"
                disabled={busy}
                onClick={remove}
                className="mt-auto inline-flex items-center gap-2 self-start text-sm font-semibold text-red-600 hover:underline disabled:opacity-40"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};
